import { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  Input,
  InputBase,
  ScrollArea,
  TextInput,
  useVirtualizedCombobox,
} from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { OPTION_HEIGHT, OPTION_LIST_PADDING } from './helpers/constants';
import { getCurrentItem } from './helpers/selectHelpers';
import {
  DynamicWidthSettings,
  useDynamicWidth,
} from './helpers/useDynamicWidth';
import { FormError, SelectIcon } from './Form';
import { SelectOption } from './SelectOption';

import inputStyles from '../input/input.module.scss';
import dropdownStyles from './dropdown.module.scss';

export type SelectionValue = string | boolean | null;

export type SingleSelectProps<Data = unknown> = {
  className?: string;
  clearable?: boolean;
  data: Array<{ label: string; value: Data }>;
  /** initial value (uncontrolled) */
  defaultValue?: Data;
  description?: ReactNode | ReactNode[];
  disabled?: boolean;
  dynamicWidthSettings?: DynamicWidthSettings;
  error?: string | boolean;
  inlineError?: boolean;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  /** called for each change in selection */
  onChange?: (value: Data | null) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  role?: string;
  searchable?: boolean;
  style?: CSSProperties;
  title?: string;
  /** selection value (controlled) */
  value: Data | null;
  /** max height of dropdown scrollcontainer */
  dropdownHeight?: number | string;
  withinPortal?: boolean;
  variant?: 'regular' | 'ghost';
  tableLayout?: boolean;
};

export function SingleSelect<Data>({
  className,
  clearable = true,
  data,
  defaultValue,
  description,
  disabled = false,
  dropdownHeight = '40vh',
  dynamicWidthSettings,
  error,
  inlineError = false,
  label,
  loading = false,
  onChange,
  placeholder,
  readOnly = false,
  required,
  role = 'select',
  searchable = true,
  style,
  tableLayout,
  title,
  value,
  variant = 'regular',
  withinPortal = true,
}: SingleSelectProps<Data>) {
  const id = useId();
  const [selection, setSelection] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });
  const currentItem = getCurrentItem(data, String(selection));
  const [shouldFilterOptions, setShouldFilterOptions] = useState(false);
  const [search, setSearch] = useState(
    currentItem ? currentItem.label || currentItem.value : ''
  );
  const [opened, setOpened] = useState(false);
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const { t } = useTranslation();

  const filteredOptions = useMemo(
    () =>
      shouldFilterOptions
        ? data.filter((item) =>
            (item.label || String(item.value))
              .toLowerCase()
              .includes(String(search).toLowerCase().trim())
          )
        : data,
    [data, search, shouldFilterOptions]
  );

  const activeOptionIndex = useMemo(
    () =>
      selection != null
        ? filteredOptions.findIndex((item) => item.value === selection)
        : -1,
    [filteredOptions, selection]
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollParent,
    estimateSize: () => OPTION_HEIGHT,
    overscan: 5,
  });

  function onOptionSubmit(index: number) {
    const newItem = filteredOptions[index];
    if (newItem && newItem.value !== currentItem?.value) {
      setSelection(newItem.value);
      setSearch(newItem.label || newItem.value);
    }
    combobox.closeDropdown();
    combobox.targetRef.current?.blur();
  }

  const combobox = useVirtualizedCombobox({
    opened,
    onOpenedChange: (isOpen) => {
      setOpened(isOpen);
    },
    onDropdownClose: () => {
      setShouldFilterOptions(false);
    },
    onDropdownOpen: () => {
      const currentItem = getCurrentItem(data, String(selection));
      if (currentItem) {
        setSearch(currentItem.label ?? currentItem.value);
      } else {
        setSearch('');
      }
      // force scroll, just in case the backend refreshed the data and the index might have moved.
      if (activeOptionIndex !== -1) {
        virtualizer.scrollToIndex(activeOptionIndex, { align: 'auto' });
      } else {
        virtualizer.scrollToIndex(0, { align: 'end' });
      }
    },
    totalOptionsCount: filteredOptions.length,
    getOptionId: (index) => `${id}-option-${index}`,
    selectedOptionIndex,
    activeOptionIndex,
    setSelectedOptionIndex: (index) => {
      setSelectedOptionIndex(index);
      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    },
    onSelectedOptionSubmit: onOptionSubmit,
  });

  const width = useDynamicWidth(
    dynamicWidthSettings,
    filteredOptions.map((item) => item.label || String(item.value))
  );

  let currentInputValue = '';
  if (opened) {
    currentInputValue = String(search);
  } else if (selection != null && currentItem) {
    currentInputValue = currentItem?.label ?? currentItem?.value;
  }

  const clearButton = (
    <ClearButton
      disabled={disabled || readOnly}
      loading={loading}
      onClear={() => {
        if (selection != null) {
          setSelection(null);
        }
      }}
    />
  );

  const selectIcon = (
    <SelectIcon
      disabled={loading || disabled || readOnly}
      error={!!error}
      variant={variant === 'ghost' ? 'primary' : undefined}
    />
  );

  let largeRightSection = false;
  let rightSection: ReactNode | undefined = selectIcon;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="loading-indicator" />;
  } else if (selection != null && clearable && !disabled) {
    largeRightSection = true;
    rightSection = (
      <>
        {clearButton}
        {selectIcon}
      </>
    );
  }

  const sharedInputProps = {
    'data-testid': 'single-select',
    classNames: {
      error: inputStyles['error'],
      input: clsx(
        inputStyles['input'],
        variant === 'ghost' && inputStyles['input--primary'],
        error && inputStyles['input--error'],
        loading && inputStyles['input--is-loading']
      ),
      section: inputStyles['section'],
      wrapper: clsx(
        inputStyles['wrapper'],
        largeRightSection && inputStyles['wrapper--large-right-section']
      ),
    },
    disabled: loading || disabled,
    readOnly: readOnly,
    error: error && (
      <FormError
        error={typeof error === 'string' ? error : undefined}
        inline={inlineError}
      />
    ),
    id,
    onClick: () => combobox.openDropdown(),
    onFocus: () => combobox.openDropdown(),
    onBlur: () => combobox.closeDropdown(),
    placeholder,
    rightSection,
    unstyled: true,
  };

  return (
    <div className={className} title={title} role={role}>
      <Combobox
        width={width}
        position="bottom-start"
        store={combobox}
        disabled={disabled || loading || readOnly}
        readOnly={readOnly}
        withinPortal={withinPortal}
        resetSelectionOnOptionHover={false}
        classNames={{
          dropdown: dropdownStyles['dropdown'],
        }}
      >
        <div
          className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}
        >
          {label && (
            <FormLabel
              description={description}
              disabled={disabled}
              error={error}
              id={id}
              inline={inlineError}
              label={label}
              loading={loading}
              required={required}
            />
          )}
          <Combobox.Target>
            {searchable ? (
              <TextInput
                {...sharedInputProps}
                rightSectionPointerEvents="none"
                value={currentInputValue}
                onChange={(event) => {
                  combobox.openDropdown();
                  setShouldFilterOptions(true);
                  setSearch(event.currentTarget.value);
                  setSelectedOptionIndex(-1);
                }}
              />
            ) : (
              <InputBase
                {...sharedInputProps}
                rightSectionPointerEvents="none"
                component="button"
                type="button"
              >
                {currentItem?.label ??
                  (currentItem?.value
                    ? String(currentItem?.value)
                    : undefined) ?? (
                    <Input.Placeholder
                      className={inputStyles['input-placeholder']}
                    >
                      {placeholder}
                    </Input.Placeholder>
                  )}
              </InputBase>
            )}
          </Combobox.Target>
        </div>

        <Combobox.Dropdown>
          <Combobox.Options style={{ width: style?.width }}>
            {filteredOptions.length > 0 ? (
              <ScrollArea.Autosize
                mah={dropdownHeight}
                type="scroll"
                scrollbarSize={4}
                viewportRef={setScrollParent}
                onMouseDown={(event) => event.preventDefault()}
              >
                <div
                  style={{
                    height:
                      // refer ./helpers/constants.ts for OPTION_LIST_PADDING usage
                      virtualizer.getTotalSize() + OPTION_LIST_PADDING * 2,
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = filteredOptions[virtualItem.index];
                    return (
                      <SelectOption
                        key={String(item.value)}
                        value={String(item.value)}
                        active={virtualItem.index === activeOptionIndex}
                        selected={virtualItem.index === selectedOptionIndex}
                        onClick={() => onOptionSubmit(virtualItem.index)}
                        variant={variant === 'ghost' ? 'primary' : undefined}
                        label={item.label || String(item.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: virtualItem.size,
                          transform: `translateY(${virtualItem.start + OPTION_LIST_PADDING}px)`,
                        }}
                      />
                    );
                  })}
                </div>
              </ScrollArea.Autosize>
            ) : (
              <Combobox.Empty>
                {search !== ''
                  ? t('app.select.noOptionsFound')
                  : t('app.select.noOptionsAvailable')}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
